import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, LogOut, LayoutDashboard, UserCircle2, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/parents", label: t("nav.parents") },
    { to: "/educators", label: t("nav.educators") },
    { to: "/about", label: t("nav.about") },
  ];

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-[#E5EBE7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="nav-logo">
            <Logo size={40} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `nav-link text-sm font-medium text-[#0A2518]/80 hover:text-[#16A34A] transition-colors ${
                    isActive ? "active text-[#16A34A]" : ""
                  }`
                }
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Right CTA */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {user && typeof user === "object" ? (
              <>
                {user.role === "admin" && (
                  <Button
                    asChild
                    variant="ghost"
                    className="text-[#0A2518] hover:text-[#16A34A] hover:bg-[#F0FDF4]"
                    data-testid="nav-admin-btn"
                  >
                    <Link to="/admin">{t("nav.admin")}</Link>
                  </Button>
                )}
                {(user.role === "parent" || user.role === "educator") && (
                  <Button
                    asChild
                    variant="ghost"
                    className="text-[#0A2518] hover:text-[#16A34A] hover:bg-[#F0FDF4]"
                    data-testid="nav-messages-btn"
                  >
                    <Link to="/messages">
                      <MessageCircle className="h-4 w-4 mr-1.5" /> {t("nav.messages")}
                    </Link>
                  </Button>
                )}
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4] hover:text-[#15803D]"
                  data-testid="nav-dashboard-btn"
                >
                  <Link to="/dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    {t("nav.myspace")}
                  </Link>
                </Button>
                <Button
                  onClick={onLogout}
                  variant="ghost"
                  className="text-[#0A2518] hover:bg-[#F0FDF4]"
                  data-testid="nav-logout-btn"
                  title={t("nav.logout")}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  asChild
                  variant="ghost"
                  className="text-[#0A2518] hover:text-[#16A34A] hover:bg-[#F0FDF4]"
                  data-testid="nav-login-btn"
                >
                  <Link to="/login">
                    <UserCircle2 className="h-4 w-4 mr-2" />
                    {t("nav.login")}
                  </Link>
                </Button>
                <Button
                  asChild
                  className="rounded-full bg-white text-[#16A34A] border border-[#16A34A] hover:bg-[#16A34A] hover:text-white shadow-sm"
                  data-testid="nav-myspace-btn"
                >
                  <Link to="/register">{t("nav.myspace")}</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-[#F0FDF4]"
            onClick={() => setOpen(!open)}
            aria-label={t("nav.menu")}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6 text-[#0A2518]" /> : <Menu className="h-6 w-6 text-[#0A2518]" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden pb-6 space-y-2" data-testid="nav-mobile-menu">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block py-2 text-base font-medium ${
                    isActive ? "text-[#16A34A]" : "text-[#0A2518]/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-3">
              <LanguageSwitcher variant="compact" />
            </div>
            <div className="pt-3 flex gap-2">
              {user && typeof user === "object" ? (
                <>
                  <Button
                    asChild
                    className="flex-1 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white"
                  >
                    <Link to="/dashboard" onClick={() => setOpen(false)}>{t("nav.myspace")}</Link>
                  </Button>
                  <Button onClick={() => { onLogout(); setOpen(false); }} variant="outline">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button asChild variant="outline" className="flex-1 border-[#16A34A] text-[#16A34A]">
                    <Link to="/login" onClick={() => setOpen(false)}>{t("nav.login")}</Link>
                  </Button>
                  <Button asChild className="flex-1 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
                    <Link to="/register" onClick={() => setOpen(false)}>{t("nav.myspace")}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
